import React, { useEffect, useState } from 'react';
import DateTimePicker from '../../components/DateTimePicker';
import MultiSelect from '../../components/MultiSelect';
import service, { type OutstandingExport, type SageExportResult, type SageFamily } from '../../services/sage.service';
import { calendarDate, dateParam, reportError, REPORT_BUTTON } from './reports/reportUtils';

/**
 * The four exports. Each selects records up to an ending invoice date, writes a file and
 * records what it covered — but marks NOTHING exported. That happens in Confirm, which
 * is why an unconfirmed export of the same family is a warning here: run it again and
 * the second file covers the same records as the first.
 *
 * All or nothing, as V1 is: one line that cannot be coded and there is no file. The
 * errors come back grouped by pro rather than as a wall of red text.
 */
const COST_TYPES = [
  'CLEANING', 'FREIGHT BILL', 'LATE GATE FEE', 'LOST/STOLEN', 'OVER SIZE PERMIT',
  'OVER WIDTH PERMIT', 'PER DIEM', 'REEFER FUEL', 'SSLINE CHASSIS USE', 'STORAGE',
  'STORAGE - CC', 'SWING/FLIP', 'WAREHOUSE ERROR', 'OTHER',
];

const BLURB: Record<SageFamily, string> = {
  invoices: 'Customer invoices and warehouse storage, coded to their GL accounts. This is '
    + 'the only export that feeds the Sage Export Details report.',
  driverpay: 'Driver pay invoices and escrow payments, net of commission, with direct-deposit '
    + 'balances zeroed out.',
  rebills: 'Rebills and credits, applied against invoices that have already been billed.',
  addlcost: 'Approved vendor costs, combined into one line per GL account on each vendor '
    + 'invoice. One calendar month at a time.',
};

/** Group the errors by the pro they belong to, so a bad batch reads as a work list. */
function byPro(errors: string[]): [string, string[]][] {
  const groups = new Map<string, string[]>();
  for (const error of errors) {
    const pro = /\b(\d{6,8})\b/.exec(error)?.[1] ?? 'Other';
    groups.set(pro, [...(groups.get(pro) ?? []), error]);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

const SageExportCard: React.FC<{ family: SageFamily }> = ({ family }) => {
  const [enddate, setEnddate] = useState<Date | null>(null);
  const [costtypes, setCosttypes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SageExportResult | null>(null);
  const [outstanding, setOutstanding] = useState<OutstandingExport[]>([]);
  const [downloaded, setDownloaded] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    service.getOutstanding(family)
      .then((rows) => { if (!cancelled) setOutstanding(rows); })
      .catch(() => { /* the warning is advisory; the export still works without it */ });
    return () => { cancelled = true; };
  }, [family, result]);

  const run = async () => {
    if (!enddate) { setError('Enter an ending invoice date.'); return; }
    setBusy(true); setError(null); setResult(null); setDownloaded(null);
    try {
      setResult(await service.runExport(family, dateParam(enddate), costtypes));
    } catch (e) {
      setError(reportError(e, 'The export failed. Please try again.'));
    } finally { setBusy(false); }
  };

  const download = async (recno: number, filename: string) => {
    try {
      setDownloaded(await service.downloadExport(recno, filename));
    } catch (e) {
      setError(reportError(e, 'Could not download the file.'));
    }
  };

  const errors = result?.errors ?? [];
  return (
    <div className="flex-1 min-h-0 p-4 space-y-4 text-label overflow-auto">
      <p className="text-sm text-muted">{BLURB[family]}</p>

      {outstanding.length > 0 && (
        <div className="rounded border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm">
          <div className="font-semibold text-amber-800 dark:text-amber-200">
            {outstanding.length} earlier export{outstanding.length === 1 ? '' : 's'} not confirmed yet
          </div>
          <p className="text-xs text-amber-800 dark:text-amber-200 mt-1">
            Records are not marked exported until Confirm runs, so anything in these files is
            still eligible and will be exported again.
          </p>
          <ul className="mt-1 text-xs font-mono">
            {outstanding.map((o) => (
              <li key={o.recno}>
                {o.fileid} — {o.recordcount} records, {new Date(o.exportdatetime).toLocaleString()}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-4 items-end flex-wrap">
        <DateTimePicker mode="date" labelType="custom" label="Ending invoice date"
          ariaLabel="Ending invoice date" value={enddate}
          onChange={(v) => setEnddate(calendarDate(v))} disabled={busy} usePortal autoFlip />
        {family === 'addlcost' && (
          <div className="min-w-[16rem]">
            <div className="text-sm mb-1">Type</div>
            {/* Portalled: this card is a scrollport (overflow-auto), so an inline
                absolute dropdown is clipped by it and opening one scrolls the card
                instead of overlaying it. Same reason InvoiceSearchCard portals its
                pickers. */}
            <MultiSelect options={COST_TYPES.map((t) => ({ value: t, label: t }))}
              value={costtypes} onChange={setCosttypes} disabled={busy}
              placeholder="All types" enableSearch usePortal />
          </div>
        )}
        <button className={REPORT_BUTTON} onClick={run} disabled={busy}>
          {busy ? 'Exporting…' : 'Export'}
        </button>
      </div>

      {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}

      {errors.length > 0 && (
        <div className="rounded border border-red-400 bg-red-50 dark:bg-red-950/30 p-3 space-y-2">
          <div className="text-sm font-semibold text-red-700 dark:text-red-300">
            Nothing was exported — {errors.length} problem{errors.length === 1 ? '' : 's'} to fix first.
          </div>
          <div className="space-y-2 max-h-96 overflow-auto">
            {byPro(errors).map(([pro, list]) => (
              <div key={pro}>
                <div className="text-xs font-semibold text-red-700 dark:text-red-300">
                  {pro === 'Other' ? 'Other' : `Pro ${pro}`}
                </div>
                <ul className="text-xs text-red-700 dark:text-red-300 list-disc pl-5">
                  {list.map((e) => <li key={e}>{e}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {result && errors.length === 0 && (
        result.empty ? (
          <div className="text-sm text-muted">Nothing to export up to that date.</div>
        ) : (
          <div className="rounded border border-theme-border-primary p-3 space-y-2">
            <div className="text-sm">
              <strong>{result.recordcount.toLocaleString()}</strong> records ·{' '}
              <strong>{result.total}</strong> · <span className="font-mono">{result.filename}</span>
            </div>
            <div className="flex gap-3 items-center">
              <button className={REPORT_BUTTON} onClick={() => download(result.recno, result.filename)}>
                Download file
              </button>
              {downloaded && <span className="text-sm text-muted">Saved {downloaded}</span>}
            </div>
            <p className="text-xs text-muted">
              Import this into Sage, then confirm it under Confirm Export to Sage — that is what
              marks the records exported. The file stays downloadable from there.
            </p>
          </div>
        )
      )}
    </div>
  );
};

export default SageExportCard;
